import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  getInternalProductVersions,
  getProductVersion,
  getProductVersions,
} from '../services/catalog-operations.service';

/**
 * Version timeline for a FEED product — the raw connector row.
 *
 * A timeline only exists once a run has re-ingested the product under the wider
 * ingest scope; before that the endpoint answers with the live version alone.
 * `enabled` keeps the request off surfaces that have no product id yet.
 */
export function useProductVersions(feedProductId: string | null) {
  return useQuery({
    queryKey: queryKeys.curation.productVersions(feedProductId ?? ''),
    queryFn: () => getProductVersions(feedProductId ?? ''),
    enabled: !!feedProductId,
  });
}

/**
 * One version of a feed product, live or archived.
 *
 * Archived versions are immutable — once superseded, a version's payload can
 * never change — so this is cached indefinitely. The live version is the one
 * exception, and it is refetched by the detail query that owns it rather than
 * here.
 */
export function useProductVersion(feedProductId: string | null, version: number | null) {
  return useQuery({
    queryKey: queryKeys.curation.productVersion(feedProductId ?? '', version ?? 0),
    queryFn: () => getProductVersion(feedProductId ?? '', version ?? 0),
    enabled: !!feedProductId && version !== null,
    staleTime: Infinity,
  });
}

/**
 * Version timeline for an INTERNAL (curated) product.
 *
 * A different entity from the feed product, with its own counter: a feed-only
 * change (price, availability) versions the feed row and leaves this one alone.
 * Passing a feed-product id here returns someone else's history, or nothing —
 * it will look plausible either way, so the id must come from
 * `product.id` on the detail payload, not from the row id.
 */
export function useInternalProductVersions(productId: string | null) {
  return useQuery({
    queryKey: queryKeys.curation.internalProductVersions(productId ?? ''),
    queryFn: () => getInternalProductVersions(productId ?? ''),
    enabled: !!productId,
  });
}
